// SAV-92A remediation -- auto-resolve lifecycle_instance_id from existing
// dashboard evidence, scoped strictly to the work item being previewed.
//
// The Engineering Dashboard already carries lifecycle_instance_id in two
// places for the currently inspected work item: the SAV-86 lifecycle
// inspection envelope and the SAV-79 QA evidence report. Both are keyed by
// their own work_item_key, and the dashboard can simultaneously hold
// evidence for different work items (see the existing QA/lifecycle scope
// mismatch note in EngineeringDashboard.jsx). This resolver never returns an
// instance id unless it can prove the source evidence's work_item_key
// matches the work item being previewed, and never fabricates or guesses a
// value when no matching evidence exists.

function isNonEmptyString(value) {
    return typeof value === "string" && value.trim().length > 0;
}

function lifecycleInspectionCandidate(inspection) {
    if (!inspection || typeof inspection !== "object") return null;
    const identity = inspection.lifecycle_identity || inspection.inspected_lifecycle || inspection;
    if (!identity || typeof identity !== "object") return null;
    const workItemKey = isNonEmptyString(identity.work_item_key) ? identity.work_item_key : null;
    const lifecycleInstanceId = isNonEmptyString(identity.lifecycle_instance_id) ? identity.lifecycle_instance_id : null;
    if (!workItemKey || !lifecycleInstanceId) return null;
    return { workItemKey, lifecycleInstanceId, source: "lifecycle_inspection" };
}

function qaReportCandidate(report) {
    if (!report || typeof report !== "object") return null;
    const workItemKey = isNonEmptyString(report.work_item_key) ? report.work_item_key : null;
    const lifecycleInstanceId = isNonEmptyString(report.lifecycle_instance_id) ? report.lifecycle_instance_id : null;
    if (!workItemKey || !lifecycleInstanceId) return null;
    return { workItemKey, lifecycleInstanceId, source: "qa_report" };
}

// Returns one of:
//   { status: "resolved", lifecycleInstanceId, source }
//   { status: "unavailable", lifecycleInstanceId: null, source: null }
//   { status: "ambiguous", lifecycleInstanceId: null, source: null }
//
// "ambiguous" means two matching sources disagree on the instance id for the
// same work item -- never pick one silently, since either could be stale or
// wrong.
export function resolveLifecycleInstanceId(data, workItemKey) {
    if (!isNonEmptyString(workItemKey)) {
        return { status: "unavailable", lifecycleInstanceId: null, source: null };
    }

    const candidates = [
        lifecycleInspectionCandidate(data?.lifecycle_inspection),
        qaReportCandidate(data?.qa_report),
    ]
        .filter(Boolean)
        .filter((candidate) => candidate.workItemKey === workItemKey);

    if (!candidates.length) {
        return { status: "unavailable", lifecycleInstanceId: null, source: null };
    }

    const distinctIds = new Set(candidates.map((candidate) => candidate.lifecycleInstanceId));
    if (distinctIds.size > 1) {
        return { status: "ambiguous", lifecycleInstanceId: null, source: null };
    }

    return { status: "resolved", lifecycleInstanceId: candidates[0].lifecycleInstanceId, source: candidates[0].source };
}
